/**
 * 物品表与器物表（纯数据，驱动背包 / 建造 / 交互）。
 * 后续加内容只需要往这两张表里添条目，不用改系统代码。
 */

/** 物品：可堆叠、可交易、可消耗 */
export const ITEMS = {
  coin: { name: '铜钱', desc: '洞天市集通用。', stack: 9999 },
  wood: { name: '木头', desc: '建房造器的基础。', stack: 99 },
  stone: { name: '石块', desc: '垒灶砌墙。', stack: 99 },
  cloth: { name: '粗布', desc: '缝被制衣。', stack: 99 },
  herb: { name: '野草', desc: '随处可见，可入药可喂鸡。', stack: 99 },
  seed: { name: '菜种', desc: '春播秋收。', stack: 99 },
  rice_seed: { name: '谷种', desc: '下在水田里，秋后割稻。', stack: 99 },
  paddy: { name: '新谷', desc: '刚割下来的稻子，舂过才是米。', stack: 99 },
  veg: { name: '青菜', desc: '清炒最香。', stack: 99 },
  rice: { name: '糙米', desc: '填饱肚子。（早市四钱一升，或自己插一畦稻）', stack: 99 },
  water: { name: '清水', desc: '淘洗、浇灌、煮饭都要它。', stack: 99 },
  meal: { name: '热饭', desc: '恢复饱腹与精力。', stack: 99 },
  talisman: { name: '符纸', desc: '写上符文可镇宅。', stack: 99 },
  jade: { name: '灵玉', desc: '洞天深处偶得。', stack: 99 },

  /* ---- 废品与车件（捡垃圾攒车用） ---- */
  scrap_iron: { name: '废铁块', desc: '锈得看不出原样，回炉还能用。', stack: 99 },
  scrap_wood: { name: '废木料', desc: '缺角的木板，劈开还能当柴。', stack: 99 },
  scrap_cloth: { name: '碎布头', desc: '擦手嫌脏，缝补嫌碎，搭成一堆倒有点用处。', stack: 99 },
  bolt: { name: '螺栓', desc: '一把卷了牙的旧螺栓。', stack: 99 },
  rope: { name: '麻绳', desc: '粗糙结实，捆什么都行。', stack: 99 },
  frame: { name: '车架', desc: '攒车的第一步：得先有个架子。', stack: 9 },
  tire: { name: '轮胎', desc: '四条齐全才能上路。', stack: 9 },
  engine: { name: '引擎', desc: '咳喘着响起来的时候，车才算活了。', stack: 9 },
  battery: { name: '电瓶', desc: '点亮车灯、起动引擎。', stack: 9 },
  seat_part: { name: '座椅', desc: '弹簧塌了半边，坐上去还是比站着强。', stack: 9 },
  plate_scrap: { name: '铁皮', desc: '敲一敲能补车身。', stack: 99 },
  wood_scrap: { name: '木料', desc: '还算规整的板条。', stack: 99 },
  fuel: { name: '车用灯油', desc: '车要喝这个才能跑。（杂货铺那壶「灯油」是点灯用的，别混了）', stack: 99 },
};

/**
 * 器物（场景中的家具/景物）。
 *  w,h      占格尺寸
 *  solid    是否阻挡行走
 *  layer    'floor' 铺地(不阻挡) / 'furniture' 家具
 *  interact 交互定义 { action, label, hint }
 *  cost     建造消耗
 *  state    初始状态（生长阶段、是否点亮等）
 */
export const PROP_DEFS = {
  /* ---- 室内 ---- */
  bed: {
    name: '木床', w: 2, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'sleep', label: '木床', hint: 'E 歇息' },
    cost: { wood: 6, cloth: 2 }, desc: '一夜好眠，次日精神。',
  },
  desk: {
    name: '书案', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'read', label: '书案', hint: 'E 读书' },
    cost: { wood: 4 }, desc: '读书可静心。',
  },
  stool: {
    name: '木凳', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'sit', label: '木凳', hint: 'E 坐下' },
    cost: { wood: 2 }, desc: '坐坐也好。',
  },
  stove: {
    name: '土灶', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'cook', label: '土灶', hint: 'E 炊事' },
    cost: { stone: 6, wood: 2 }, desc: '生火造饭。',
  },
  shelf: {
    name: '书架', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'bookshelf', label: '书架', hint: 'E 翻书' },
    cost: { wood: 5 }, desc: '藏书之处，自己的文章也能收进来读。', state: { books: 0 },
  },
  jar: {
    name: '水缸', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'draw', label: '水缸', hint: 'E 取水' },
    cost: { stone: 3 }, desc: '存雨水，日日不空。',
  },
  chest: {
    name: '木箱', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'store', label: '木箱', hint: 'E 收纳' },
    cost: { wood: 3 }, desc: '家里多余的东西都塞这儿。',
  },
  cushion: {
    name: '蒲团', w: 1, h: 1, solid: false, layer: 'furniture',
    interact: { action: 'meditate', label: '蒲团', hint: 'E 打坐' },
    cost: { cloth: 2 }, desc: '静坐吐纳，可养灵气。',
  },
  lamp: {
    name: '油灯', w: 1, h: 1, solid: false, layer: 'furniture',
    interact: { action: 'light', label: '油灯', hint: 'E 点灯' },
    cost: { wood: 1, talisman: 1 }, desc: '夜里的一点暖。', state: { lit: false },
  },
  rug: {
    name: '地毯', w: 2, h: 1, solid: false, layer: 'floor',
    cost: { cloth: 3 }, desc: '踩上去软和一些。',
  },
  tv: {
    name: '电视机', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'watch', label: '电视机', hint: 'E 看电视' },
    cost: { wood: 3, jade: 1 },
    desc: '一方小窗，屋里的人能一起看。',
    state: { url: '', startedAt: 0, by: '', lit: false },
  },
  gametable: {
    name: '游戏桌', w: 2, h: 2, solid: true, layer: 'furniture',
    interact: { action: 'watch', label: '游戏桌', hint: 'E 同乐' },
    cost: { wood: 6, cloth: 2, jade: 1 },
    desc: '一张方桌四条凳，桌面嵌着同样的那方小窗，屋里的人各占一边。',
    state: { url: '', startedAt: 0, by: '', lit: false },
  },
  whiteboard: {
    name: '白板', w: 1, h: 2, solid: true, layer: 'furniture',
    interact: { action: 'notes', label: '白板', hint: 'E 写写画画' },
    cost: { wood: 3, cloth: 1 },
    desc: '立在电视右手边的一块板：念头随手写上去，分门别类钉着，网址还能一键投到电视。',
    state: { cats: ['灵感', '网址'], tab: '灵感', notes: [] },
  },

  /* ---- 庭院 ---- */
  well: {
    name: '水井', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'draw', label: '水井', hint: 'E 打水' },
    cost: { stone: 8 }, desc: '院中的老井，从未干涸。',
  },
  tree: {
    name: '果树', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'forage', label: '果树', hint: 'E 采摘' },
    cost: { wood: 2, seed: 1 }, desc: '春华秋实。', state: { fruit: true },
  },
  plot: {
    name: '菜畦', w: 1, h: 1, solid: false, layer: 'floor',
    interact: { action: 'tend', label: '菜畦', hint: 'E 料理' },
    cost: { wood: 1 }, desc: '一畦青菜，一日一浇。', state: { stage: 0, watered: false },
  },
  // 水田：米的来路。与菜畦同一套流程（播种→灌水→收割），收的是新谷，
  // 新谷要在米缸上舂过才是米——「米从哪儿来」这一环才算合上。
  paddy_plot: {
    name: '水田', w: 1, h: 1, solid: false, layer: 'floor',
    interact: { action: 'tend', label: '水田', hint: 'E 料理' },
    cost: { wood: 2, stone: 2 }, desc: '一畦稻子，一日一灌。割下来是新谷，舂过才是米。',
    state: { stage: 0, watered: false },
  },
  // 米缸：舂米与存米的地方，也是灶上缺米时自动来舀的地方（action granary）
  rice_bin: {
    name: '米缸', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'granary', label: '米缸', hint: 'E 看缸' },
    cost: { wood: 2, stone: 1 }, desc: '缸里有米，心里不慌。灶上缺米时会来这儿舀。',
    state: { rice: 0 },
  },
  // 丹炉：草药与灵气的去处（action refine，见 data/alchemy.js）
  alchemy_stove: {
    name: '丹炉', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'refine', label: '丹炉', hint: 'E 炼丹' },
    cost: { stone: 4, scrap_iron: 2 }, desc: '小火慢炼，草药在这里成了丹。',
  },
  // 照心镜：修行到了「感气」才照得见自己（action introspect）
  mind_mirror: {
    name: '照心镜', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'introspect', label: '照心镜', hint: 'E 内视' },
    cost: { stone: 2, brokenGlass: 2 }, desc: '照不见脸，照的是心里攒下的东西。',
  },
  // 施饭桌：把做好的吃食端出去（action serve）——舍出去的，过些日子会回来
  charity_table: {
    name: '施饭桌', w: 1, h: 1, solid: false, layer: 'furniture',
    interact: { action: 'serve', label: '施饭桌', hint: 'E 施食' },
    cost: { wood: 3, stone: 1 }, desc: '一碗热饭放在这儿，谁饿了谁来端。',
  },
  stone_lantern: {
    name: '石灯', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'light', label: '石灯', hint: 'E 点灯' },
    cost: { stone: 4 }, desc: '夜里给院子留一盏。', state: { lit: false },
  },
  fence: {
    name: '篱笆', w: 1, h: 1, solid: true, layer: 'furniture',
    cost: { wood: 2 }, desc: '把院子圈起来。',
  },
  sign: {
    name: '木牌', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'rename', label: '木牌', hint: 'E 题字' },
    cost: { wood: 1 }, desc: '给它起个名字吧。', state: { text: '洞天' },
  },

  /* ---- 门户与城郊 ---- */
  portal: {
    name: '门户', w: 1, h: 1, solid: false, layer: 'furniture',
    interact: { action: 'goto', label: '门户', hint: 'E 前往' },
    cost: { stone: 2 }, desc: '立一块界石，通向别处。',
    state: { to: '', label: '', spawn: [] },
  },
  milestone: {
    name: '里程碑', w: 1, h: 1, solid: true, layer: 'furniture',
    cost: { stone: 3 }, desc: '写着距城多少里。', state: { text: '五里' },
  },
  signpost: {
    name: '路牌', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'rename', label: '路牌', hint: 'E 题字' },
    cost: { wood: 2 }, desc: '岔路口的一块指路木牌。', state: { text: '路口' },
  },
  notice_board: {
    name: '布告栏', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'notice', label: '布告栏', hint: 'E 看告示' },
    cost: { wood: 3 }, desc: '悬赏、招工、顺风车，都贴在这儿。',
  },
  mailbox: {
    name: '订单箱', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'store', label: '订单箱', hint: 'E 收货' },
    cost: { wood: 3 }, desc: '送货与收货都往这儿放。',
  },
  junk_pile: {
    name: '垃圾堆', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'scavenge', label: '垃圾堆', hint: 'E 翻找' },
    cost: {}, desc: '别人不要的东西，也许正是你缺的那一块。', state: { level: 1 },
  },
  car_wreck: {
    name: '弃车', w: 2, h: 2, solid: true, layer: 'furniture',
    interact: { action: 'salvage', label: '弃车', hint: 'E 拆解' },
    cost: {}, desc: '锈在路边不知多少年的车架子，零件还能拆。', state: { level: 2 },
  },
  workbench: {
    name: '工作台', w: 2, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'assemble', label: '工作台', hint: 'E 动手组装' },
    cost: { wood: 6, stone: 2 }, desc: '秤上门户一样的宽度，讲究的是顺手。',
  },
  gas_pump: {
    name: '加油机', w: 1, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'refuel', label: '加油机', hint: 'E 加油' },
    cost: { stone: 2 }, desc: '按文计费，加完记得付钱。',
  },
  crate_stack: {
    name: '货箱垛', w: 1, h: 1, solid: true, layer: 'furniture',
    cost: { wood: 3 }, desc: '码得整整齐齐的杂货。',
  },
  bench: {
    name: '长凳', w: 2, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'sit', label: '长凳', hint: 'E 坐下' },
    cost: { wood: 3 }, desc: '走累了坐一会儿。',
  },

  /* ---- 城镇铺面 ---- */
  counter: {
    name: '柜台', w: 2, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'shop', label: '柜台', hint: 'E 打问' },
    cost: { wood: 5 }, desc: '老板坐在后头，什么都能问一问。',
    state: { shop: '', kind: 'general' },
  },
  stall: {
    name: '摊位', w: 2, h: 1, solid: true, layer: 'furniture',
    interact: { action: 'shop', label: '摊位', hint: 'E 打问' },
    cost: { wood: 4, cloth: 2 }, desc: '一张布棚，几样杂货。',
    state: { shop: '', kind: 'stall' },
  },
};

/**
 * 能坐能躺的器物：姿态 + 落座点。
 *  ox, oy 是相对器物左上角的「格」偏移（乘瓦片尺寸得到像素），一般取器物中心偏下。
 *  pose: 'sit' 盘坐 / 'lie' 躺卧。
 *  facing 坐下后朝哪儿（不写就用默认：sit 朝下、lie 朝上）。
 *
 * 一件器物只有一个座就直接写 { pose, ox, oy }；
 * 想围着坐（游戏桌四条凳）就写 seats: [...]，一条一个座，谁近谁先把着。
 */
export const SEAT_DEFS = {
  cushion: { pose: 'sit', ox: 0.5, oy: 0.74 },
  stool: { pose: 'sit', ox: 0.5, oy: 0.44 },
  rug: { pose: 'sit', ox: 1.0, oy: 0.62 },
  bed: { pose: 'lie', ox: 1.0, oy: 0.52 },
  chest: { pose: 'sit', ox: 0.5, oy: 0.33 },
  bench: { pose: 'sit', ox: 1.0, oy: 0.44 },
  // 游戏桌：南北各两条凳，坐下就对着桌心
  gametable: {
    seats: [
      { pose: 'sit', ox: 0.52, oy: 0.26, facing: 'down' },
      { pose: 'sit', ox: 1.48, oy: 0.26, facing: 'down' },
      { pose: 'sit', ox: 0.52, oy: 1.52, facing: 'up' },
      { pose: 'sit', ox: 1.48, oy: 1.52, facing: 'up' },
    ],
  },
};

export function getItem(id) { return ITEMS[id] || { name: id || '未知', desc: '', stack: 99 }; }
/**
 * 查一件器物的定义。没有这一号就给个兜底：一件叫不出名字、也不挡路的东西。
 *
 * 兜底从前是实心的，于是场景里凡是写法写错、漏注册的器物（城门口那两扇「门」、
 * 街边一堆摊子）都成了画不出却挡得住的方砖，人走着走着就撞上一堵看不见的墙。
 * 摆错一件东西就断路，代价太大；宁可让人从摊子边挤过去，也别把人卡死在门口。
 * 带 unknown 标记，冒烟会把这些漏注册的一一报出来，好补齐。
 */
export function getProp(id) {
  return PROP_DEFS[id]
    || { name: id || '未知', w: 1, h: 1, solid: false, layer: 'furniture', unknown: true };
}
export function getSeat(id) { return getSeats(id)[0] || null; }

/** 一件器物的全部座位（带序号）；没有座位就回空数组 */
export function getSeats(id) {
  const def = SEAT_DEFS[id];
  if (!def) return [];
  if (Array.isArray(def.seats) && def.seats.length) {
    return def.seats.map((s, index) => ({ index, ...s }));
  }
  return [{ index: 0, ...def }];
}

/* ------------------------------------------------------------------ *
 * 运行时扩展接口：编辑器可以在游戏开着的时候改表，
 * 改完立刻在地图、布置面板、背包里生效；也能一键导出成 JS 片段。
 * ------------------------------------------------------------------ */

export const DEF_STORAGE_KEY = 'dongtianju:defs';

/** 模块原始定义，用于「删除自定义条目」时还原 */
const BASE_ITEMS = { ...ITEMS };
const BASE_PROPS = { ...PROP_DEFS };
const BASE_SEATS = { ...SEAT_DEFS };

/** 器物可用的兜底形状（没有专门画法时按这个画） */
export const PROP_SHAPES = [
  { id: 'box', name: '木箱' },
  { id: 'heap', name: '一堆' },
  { id: 'post', name: '立柱' },
  { id: 'barrel', name: '圆桶' },
  { id: 'machine', name: '机器' },
  { id: 'counter', name: '柜台' },
  { id: 'board', name: '立牌' },
  { id: 'car', name: '车形' },
  { id: 'pad', name: '地垫' },
];

/** 器物可用的交互动作（actions.js 里的 handler 名） */
export const PROP_ACTIONS = [
  { id: '', name: '不交互' },
  { id: 'goto', name: '前往别处' },
  { id: 'scavenge', name: '翻找垃圾' },
  { id: 'salvage', name: '拆解零件' },
  { id: 'assemble', name: '动手组装' },
  { id: 'refuel', name: '加油' },
  { id: 'repair', name: '修车' },
  { id: 'shop', name: '买卖' },
  { id: 'notice', name: '看告示' },
  { id: 'store', name: '收纳' },
  { id: 'sit', name: '坐下' },
  { id: 'sleep', name: '歇息' },
  { id: 'cook', name: '炊事' },
  { id: 'draw', name: '取水' },
  { id: 'forage', name: '采摘' },
  { id: 'tend', name: '料理' },
  { id: 'read', name: '读书' },
  { id: 'rename', name: '题字' },
  { id: 'meditate', name: '打坐' },
  { id: 'light', name: '点灯' },
  { id: 'watch', name: '看电视' },
  { id: 'bookshelf', name: '翻书' },
  { id: 'notes', name: '写白板' },
  { id: 'refine', name: '炼丹' },
  { id: 'introspect', name: '内视' },
  { id: 'serve', name: '施食' },
  { id: 'granary', name: '米缸' },
];

/** 把一条定义整理成表里的规范形式 */
function normalizeItem(def = {}) {
  return {
    name: def.name || '未命名',
    desc: def.desc || '',
    stack: Number(def.stack) || 99,
  };
}

function normalizeProp(id, def = {}) {
  return {
    name: def.name || id || '未命名',
    w: Math.max(1, Math.min(6, Number(def.w) || 1)),
    h: Math.max(1, Math.min(6, Number(def.h) || 1)),
    solid: def.solid !== false,
    layer: def.layer === 'floor' ? 'floor' : 'furniture',
    interact: def.interact && def.interact.action ? {
      action: String(def.interact.action),
      label: def.interact.label || def.name || id,
      hint: def.interact.hint || 'E 交互',
    } : undefined,
    cost: def.cost && typeof def.cost === 'object' ? { ...def.cost } : {},
    state: def.state && typeof def.state === 'object' ? JSON.parse(JSON.stringify(def.state)) : undefined,
    shape: def.shape || undefined,
    desc: def.desc || '',
  };
}

/** 新增或覆盖一条物品；返回是否成功 */
export function registerItem(id, def) {
  if (!id) return false;
  ITEMS[id] = normalizeItem(def);
  return true;
}

/** 新增或覆盖一条器物 */
export function registerProp(id, def) {
  if (!id) return false;
  PROP_DEFS[id] = normalizeProp(id, def);
  return true;
}

const FACINGS = ['up', 'down', 'left', 'right'];
const normSeat = (s) => ({
  pose: s.pose === 'lie' ? 'lie' : 'sit',
  ox: Number(s.ox) || 0.5,
  oy: Number(s.oy) || 0.5,
  facing: FACINGS.includes(s.facing) ? s.facing : undefined,
});

export function registerSeat(id, def) {
  if (!id || !def) return false;
  // 多条凳子：逐条归一化。写成 JSON 时空字段会被丢掉，所以先补默认值再从对象里删掉 undefined
  if (Array.isArray(def.seats) && def.seats.length) {
    SEAT_DEFS[id] = {
      seats: def.seats.map((s) => {
        const one = { pose: s.pose === 'lie' ? 'lie' : 'sit', ox: Number(s.ox) || 0.5, oy: Number(s.oy) || 0.5 };
        if (FACINGS.includes(s.facing)) one.facing = s.facing;
        return one;
      }),
    };
    return true;
  }
  SEAT_DEFS[id] = normSeat(def);
  return true;
}

/** 删除一条自定义定义；若是内置条目则还原为出厂值 */
export function removeDef(kind, id) {
  if (kind === 'item') {
    delete ITEMS[id];
    if (BASE_ITEMS[id]) ITEMS[id] = { ...BASE_ITEMS[id] };
  } else if (kind === 'prop') {
    delete PROP_DEFS[id];
    if (BASE_PROPS[id]) PROP_DEFS[id] = { ...BASE_PROPS[id] };
    delete SEAT_DEFS[id];
    if (BASE_SEATS[id]) SEAT_DEFS[id] = { ...BASE_SEATS[id] };
  }
}

/** 去掉某个器物的坐姿：从「可坐」改回「不可坐」 */
export function removeSeat(id) {
  delete SEAT_DEFS[id];
  if (BASE_SEATS[id]) SEAT_DEFS[id] = { ...BASE_SEATS[id] };
}

export function isBuiltin(kind, id) {
  return kind === 'item' ? !!BASE_ITEMS[id] : !!BASE_PROPS[id];
}

/** 导出当前两张表（含内置），用于写文件或给 judge 判卷 */
export function exportDefs() {
  return {
    items: JSON.parse(JSON.stringify(ITEMS)),
    props: JSON.parse(JSON.stringify(PROP_DEFS)),
    seats: JSON.parse(JSON.stringify(SEAT_DEFS)),
  };
}

/** 只导出与出厂不同的部分（自增改），体量小、便于贴回代码 */
export function exportCustomDefs() {
  const items = {}, props = {}, seats = {};
  for (const [k, v] of Object.entries(ITEMS)) {
    if (JSON.stringify(v) !== JSON.stringify(BASE_ITEMS[k])) items[k] = v;
  }
  for (const [k, v] of Object.entries(PROP_DEFS)) {
    if (JSON.stringify(v) !== JSON.stringify(BASE_PROPS[k])) props[k] = v;
  }
  for (const [k, v] of Object.entries(SEAT_DEFS)) {
    if (JSON.stringify(v) !== JSON.stringify(BASE_SEATS[k])) seats[k] = v;
  }
  return { items, props, seats };
}

/** 载入一组自定义定义 */
export function loadDefs(ext = {}) {
  let n = 0;
  for (const [k, v] of Object.entries(ext.items || {})) { registerItem(k, v); n++; }
  for (const [k, v] of Object.entries(ext.props || {})) { registerProp(k, v); n++; }
  for (const [k, v] of Object.entries(ext.seats || {})) { registerSeat(k, v); n++; }
  return n;
}

/** 从 localStorage 读回上次编辑的定义 */
export function loadDefsFromStorage() {
  try {
    const raw = localStorage.getItem(DEF_STORAGE_KEY);
    if (!raw) return 0;
    return loadDefs(JSON.parse(raw));
  } catch { return 0; }
}

export function saveDefsToStorage() {
  try {
    localStorage.setItem(DEF_STORAGE_KEY, JSON.stringify(exportCustomDefs()));
    return true;
  } catch { return false; }
}
